import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSmoking, FaShieldAlt, FaHeart, FaCheck, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaCertificate } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import addictionsImage from '../../components/assets/Staff/SMOKING AND ADDICTIONS1.jpg';

const Addictions = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const supportRef = useRef(null);
  const researchRef = useRef(null);
  const benefitsRef = useRef(null);
  const qualificationsRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'support': supportRef,
      'research': researchRef,
      'benefits': benefitsRef,
      'qualifications': qualificationsRef,
      'summary': summaryRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'support', ref: supportRef },
        { id: 'research', ref: researchRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'qualifications', ref: qualificationsRef },
        { id: 'summary', ref: summaryRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Holistic Perspective', icon: FaSmoking },
    { id: 'support', label: 'Support During Change', icon: FaHeart },
    { id: 'research', label: 'Research & Evidence', icon: FaBookOpen },
    { id: 'benefits', label: 'Benefits of Stopping', icon: FaCheck },
    { id: 'qualifications', label: 'Professional Training', icon: FaCertificate },
    { id: 'summary', label: 'In Summary', icon: FaLightbulb }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Physical & Psychological",
      description: "Smoking addiction has both physical and psychological components; nicotine interacts with the brain's reward system"
    },
    {
      icon: FaShieldAlt,
      title: "Complementary Support",
      description: "This frequency-based method is not used to diagnose addiction or replace established smoking cessation programmes"
    },
    {
      icon: FaSmoking,
      title: "Balance & Stress",
      description: "Applied to support balance, help reduce stress burden and assist during periods of behavioural change"
    }
  ];

  const supportReasons = [
    "feel stuck in repeated quit-and-relapse cycles",
    "experience strong stress or emotional triggers linked to smoking",
    "want additional non-invasive support alongside behavioural or medical approaches",
    "are looking for a holistic, personalised method during lifestyle change"
  ];

  const timelineData = [
    { time: '20 minutes', benefit: 'Heart rate and blood pressure begin to drop' },
    { time: '8 hours', benefit: 'Oxygen levels rise and carbon monoxide levels decrease' },
    { time: '24 hours', benefit: 'Risk of heart attack begins to decline' },
    { time: '48 hours', benefit: 'Senses of taste and smell start to return' },
    { time: '2 weeks to 3 months', benefit: 'Circulation and lung function improve' },
    { time: '1 year', benefit: 'Risk of coronary heart disease is approximately halved' },
    { time: '5–10 years', benefit: 'Significantly reduced risk of smoking-related cancers' }
  ];

  const additionalSources = [
    {
      title: 'NHS – stop smoking guidance',
      url: 'https://www.nhs.uk/common-health-questions/lifestyle/what-can-i-do-to-stop-smoking/'
    },
    {
      title: 'American Cancer Society – benefits of quitting smoking',
      url: 'https://www.cancer.org/healthy/stay-away-from-tobacco/benefits-of-quitting-smoking-over-time.html'
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Addictions / Stop Smoking" description="Bioresonance support for smoking cessation and addictions at Bioresonance Leicester. NCSCT-certified. Complementary support at 108 New Walk." path="/therapy/bioresonance-therapy/addictions" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
          <ol className="flex items-center space-x-2 text-sm text-dark-grey">
            <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
            <li className="text-gold">/</li>
            <li className="text-gold font-semibold">Addictions</li>
          </ol>
        </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaSmoking className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                SMOKING AND ADDICTIONS
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed font-semibold">
                Supporting change and balance in a complementary way
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                For many people, smoking is far more than a habit. It is often closely linked to routines, emotions, stress management and the nervous system. Even when motivation to quit is strong, the process itself can feel overwhelming, frustrating and emotionally demanding.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                This is why some individuals, alongside conventional stop-smoking methods, explore complementary and non-pharmacological approaches that focus on supporting both the body and mind during periods of change.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such option, applied in practice as a frequency-based approach.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>120 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={addictionsImage}
                      alt="Smoking and addictions support"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs - pill style to match NLS Hunter / Skin Problems */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding smoking addiction from a holistic perspective
              </h2>
              <p className="text-lg text-gray-600">
                From a holistic perspective, addiction is not viewed solely as a chemical dependency, but as a complex interaction between the nervous system, emotional regulation, habits and the overall stress load placed on the body.
              </p>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Smoking addiction is commonly understood as having both physical and psychological components. Nicotine interacts with the brain's reward system, reinforcing behavioural patterns and emotional associations. Over time, smoking may also become deeply embedded in daily routines and stress-response mechanisms.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Awareness callout - matches NLS Hunter / Skin Problems */}
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    Complementary support. Not replacement.
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Within complementary wellness settings, this frequency-based method is not used to diagnose addiction or replace established smoking cessation programmes. Instead, it is applied to support balance, help reduce stress burden and assist individuals during periods of behavioural change.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support during lifestyle change */}
      <section ref={supportRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Support during lifestyle change
              </h2>
              <p className="text-lg text-gray-600">
                Sessions using this frequency-based approach are personalised and informed by the individual's unique responses.
              </p>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              This acknowledges that each person experiences addiction differently and that responses to complementary methods can vary widely.
            </p>
            
            <p className="text-gray-600 mb-4 leading-relaxed font-semibold">
              Some individuals choose this form of support when they:
            </p>
            <ul className="space-y-3 text-gray-600 leading-relaxed list-none">
              {supportReasons.map((reason, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gold mr-3 mt-1">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Scientific research and observational evidence */}
      <section ref={researchRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Scientific research and observational evidence
              </h2>
              <p className="text-lg text-gray-600">
                A pilot study published in Forschende Komplementärmedizin in 2014 explored the use of a bioresonance-based method in smoking cessation.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-8">
              <h3 className="text-2xl font-bold text-dark-grey mb-4">Study</h3>
              <p className="text-gray-600 mb-6 font-medium leading-relaxed">
                The study, titled <em>Evidence for the Efficacy of a Bioresonance Method in Smoking Cessation</em>, was a prospective, placebo-controlled, double-blind, parallel-group design involving 190 smokers, with 95 participants in the bioresonance group and 95 in the placebo group.
              </p>
              
              <h4 className="font-semibold text-dark-grey mb-3">Reported outcomes were as follows:</h4>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>After one week, a 77.2% success rate was reported in the bioresonance group compared to 54.8% in the placebo group.</li>
                <li>After one month, 51.1% of participants in the bioresonance group reported success compared to 28.6% in the placebo group.</li>
                <li>After one year, 28.6% of participants in the bioresonance group reported success compared to 16.1% in the placebo group.</li>
                <li>No adverse effects were reported during the study.</li>
              </ul>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-dark-grey mb-2">Full publication:</h4>
                  <a 
                    href="https://karger.com/fok/article/21/4/239/356728/Evidence-for-the-Efficacy-of-a-Bioresonance-Method"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue hover:text-gold transition-colors duration-200 text-sm break-all"
                  >
                    https://karger.com/fok/article/21/4/239/356728/Evidence-for-the-Efficacy-of-a-Bioresonance-Method
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold text-dark-grey mb-2">Additional reference:</h4>
                  <a 
                    href="https://benthamopen.com/FULLTEXT/TOEPIJ-8-1#r17"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue hover:text-gold transition-colors duration-200 text-sm break-all"
                  >
                    https://benthamopen.com/FULLTEXT/TOEPIJ-8-1#r17
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-6 border border-gold/20">
              <p className="text-gray-600 leading-relaxed">
                It is important to note that this was a pilot study and that further large-scale, independent research is required to confirm long-term effectiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of stopping smoking – general health perspective */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Benefits of stopping smoking – general health perspective
              </h2>
              <p className="text-lg text-gray-600">
                The health benefits of stopping smoking are well documented in mainstream medical literature and occur progressively over time.
              </p>
            </div>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              These benefits are associated with quitting smoking itself, regardless of the method used. Examples include:
            </p>
            
            {/* Timeline */}
            <div className="relative max-w-4xl mx-auto">
              {/* Timeline Line */}
              <motion.div 
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-gold via-yellow to-gold rounded-full"
              ></motion.div>
              
              {/* Timeline Items */}
              <div className="space-y-8">
                {timelineData.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
                    whileHover={{ x: 5 }}
                    className="relative flex items-start group"
                  >
                    {/* Timeline Dot */}
                    <div className="relative z-10">
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                        className="w-16 h-16 bg-gradient-to-br from-gold to-yellow rounded-full flex items-center justify-center shadow-xl border-4 border-white"
                      >
                        <FaClock className="text-white text-lg" />
                      </motion.div>
                      
                      {/* Glow Effect */}
                      <div className="absolute inset-0 w-16 h-16 bg-gold/30 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="ml-8 flex-1">
                      <motion.div 
                        whileHover={{ y: -3 }}
                        className="bg-gray-50 rounded-xl p-6 shadow-lg border border-grey/10 hover:shadow-xl transition-all duration-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gold mb-2">{item.time}</h4>
                            <p className="text-gray-600">{item.benefit}</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <p className="text-gray-600 mt-12 mb-4 leading-relaxed font-semibold">
              More information can be found at:
            </p>
            <ul className="space-y-3">
              {additionalSources.map((source, index) => (
                <li key={index}>
                  <a 
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue hover:text-gold transition-colors duration-200"
                  >
                    {source.title}
                  </a>
                  <br />
                  <span className="text-sm text-gray-500 break-all">{source.url}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Professional background and training */}
      <section ref={qualificationsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Professional background and training
              </h2>
              <p className="text-lg text-gray-600">
                Complementary services are provided by Tomasz Chmiel, who has completed certified training through the National Centre for Smoking Cessation and Training (NCSCT).
              </p>
            </div>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              The NCSCT is a recognised organisation supporting evidence-based smoking cessation education in the UK. Completed certifications include:
            </p>
            
            <div className="space-y-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-grey/20">
                <h3 className="text-xl font-semibold text-dark-grey mb-4">Assessment of Core Knowledge and Practice Skills</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Certificate number:</strong> 321430-326955</p>
                  <p><strong>Date issued:</strong> 12 September 2024</p>
                  <p><strong>Issued by:</strong> Dr Andy McEwen, Executive Director, NCSCT</p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-grey/20">
                <h3 className="text-xl font-semibold text-dark-grey mb-4">Mental Health Specialty Module</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Certificate number:</strong> 321430-327487</p>
                  <p><strong>Date issued:</strong> 18 September 2024</p>
                  <p><strong>Issued by:</strong> Dr Andy McEwen, Executive Director, NCSCT</p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed">
              This frequency-based approach is intended as complementary support and may be used alongside conventional smoking cessation strategies.
            </p>
          </div>
        </div>
      </section>

      {/* In summary */}
      <section ref={summaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              In summary
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Bioresonance, applied as a frequency-based method using the BICOM® system, offers a non-invasive and personalised complementary option that some individuals choose to explore while working towards stopping smoking.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action - matches NLS Hunter / Skin Problems */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Support for Smoking Cessation?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Bioresonance, applied as a frequency-based method using the BICOM® system, offers a non-invasive and personalised complementary option that some individuals choose to explore while working towards stopping smoking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
            <Link
              to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              Back to Bioresonance Therapy
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default Addictions;
